/**
 * NexusDashboard - Network intelligence overview with Phase 3 analytics
 * Capacity Heatmap, Service Demand, Network Metrics Trending
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Loader2, Inbox, CheckCircle2, Users, Clock, AlertTriangle, Zap,
  TrendingDown, XCircle, Timer, MapPin, BarChart3, TrendingUp, Activity
} from "lucide-react";

const NexusDashboard = ({ token, API }) => {
  const [data, setData] = useState(null);
  const [placement, setPlacement] = useState(null);
  const [declines, setDeclines] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [serviceDemand, setServiceDemand] = useState(null);
  const [networkMetrics, setNetworkMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendDays, setTrendDays] = useState("30");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [dashRes, placeRes, declineRes, heatRes, svcRes, metRes] = await Promise.all([
        fetch(`${API}/api/nexus/analytics/dashboard`, { headers }),
        fetch(`${API}/api/nexus/analytics/time-to-placement`, { headers }),
        fetch(`${API}/api/nexus/analytics/decline-reasons`, { headers }),
        fetch(`${API}/api/nexus/analytics/capacity-heatmap`, { headers }),
        fetch(`${API}/api/nexus/analytics/service-demand`, { headers }),
        fetch(`${API}/api/nexus/analytics/network-metrics?days=${trendDays}`, { headers }),
      ]);
      const [dashData, placeData, declineData, heatData, svcData, metData] = await Promise.all([
        dashRes.json(), placeRes.json(), declineRes.json(), heatRes.json(), svcRes.json(), metRes.json(),
      ]);
      if (dashData.success) setData(dashData.data);
      if (placeData.success) setPlacement(placeData.summary);
      if (declineData.success) setDeclines(declineData.data);
      if (heatData.success) setHeatmap(heatData.data);
      if (svcData.success) setServiceDemand(svcData.data);
      if (metData.success) setNetworkMetrics(metData);
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [API, token, trendDays]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;
  }
  if (!data) return null;

  const ref = data.referrals || {};
  const cap = data.capacity || {};
  const avgPlacement = placement?.avg_hours_to_placement || 0;
  const totalPlaced = placement?.total_placed || 0;
  const netSummary = networkMetrics?.summary || {};

  const statCards = [
    { label: "Total Referrals", value: ref.total || 0, icon: Inbox, color: "text-indigo-600 bg-indigo-50" },
    { label: "Active", value: ref.active || 0, icon: Zap, color: "text-amber-600 bg-amber-50" },
    { label: "Placed", value: ref.placed || 0, icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
    { label: "Providers", value: cap.total_clinicians || 0, icon: Users, color: "text-purple-600 bg-purple-50" },
    { label: "Conversion", value: `${netSummary.conversion_rate_pct || 0}%`, icon: TrendingUp, color: "text-cyan-600 bg-cyan-50" },
  ];

  const gapColors = { critical: "bg-red-100 text-red-700 border-red-200", warning: "bg-amber-100 text-amber-700 border-amber-200", healthy: "bg-emerald-100 text-emerald-700 border-emerald-200" };

  return (
    <div data-testid="nexus-dashboard" className="space-y-4">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {statCards.map((s, i) => {
          const Icon = s.icon;
          return (
            <Card key={i} className="border-slate-200">
              <CardContent className="py-4 px-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${s.color}`}><Icon className="w-5 h-5" /></div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                    <p className="text-xs text-slate-500">{s.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Row 2: Time-to-Placement + Decline Reasons + SLA */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Timer className="w-4 h-4 text-indigo-600" /> Avg. Time to Placement</CardTitle>
          </CardHeader>
          <CardContent>
            {totalPlaced > 0 ? (
              <div className="text-center py-2">
                <p className="text-3xl font-bold text-indigo-700">
                  {avgPlacement < 24 ? `${avgPlacement.toFixed(1)}h` : `${(avgPlacement / 24).toFixed(1)}d`}
                </p>
                <p className="text-xs text-slate-500 mt-1">across {totalPlaced} placement{totalPlaced !== 1 ? "s" : ""}</p>
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No placements yet</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><TrendingDown className="w-4 h-4 text-red-500" /> Decline Reasons</CardTitle>
          </CardHeader>
          <CardContent>
            {declines && declines.length > 0 ? (
              <div className="space-y-2">
                {declines.slice(0, 5).map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1.5">
                      {d.event_type === "declined" ? <XCircle className="w-3 h-3 text-red-500" /> : <AlertTriangle className="w-3 h-3 text-amber-500" />}
                      <span className="text-slate-600 text-xs capitalize">{d.reason_code.replace(/_/g, " ")}</span>
                    </div>
                    <span className="text-xs font-medium text-slate-900">{d.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No declines recorded</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" /> SLA Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(ref.by_urgency || {}).map(([urg, count]) => {
                const slaMap = { routine: 72, urgent: 24, emergent: 4 };
                return (
                  <div key={urg} className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 capitalize">{urg}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400">{slaMap[urg] || 72}h target</span>
                      <span className="text-xs font-medium text-slate-900">{count} active</span>
                    </div>
                  </div>
                );
              })}
              {Object.keys(ref.by_urgency || {}).length === 0 && <p className="text-sm text-slate-400 text-center py-4">No active referrals</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Capacity Heatmap */}
      {heatmap && heatmap.length > 0 && (
        <Card className="border-slate-200" data-testid="capacity-heatmap">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-600" /> Capacity Heatmap by County
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {heatmap.map((h) => (
                <div
                  key={h.county}
                  data-testid={`heatmap-${h.county}`}
                  className={`p-2.5 rounded-lg border ${gapColors[h.status] || gapColors.healthy}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-xs">{h.county}</span>
                    <Badge variant="outline" className={`text-[9px] px-1 py-0 border-0 ${gapColors[h.status]}`}>
                      {h.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-[10px]">
                    <div><span className="text-slate-500">Demand:</span> <span className="font-medium">{h.demand}</span></div>
                    <div><span className="text-slate-500">Providers:</span> <span className="font-medium">{h.supply_clinicians}</span></div>
                    <div><span className="text-slate-500">Accepting:</span> <span className="font-medium">{h.supply_accepting}</span></div>
                    <div><span className="text-slate-500">Gap:</span> <span className="font-medium">{h.gap > 0 ? `+${h.gap}` : h.gap}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Row 4: Service Demand + Network Trending */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Service Demand */}
        <Card className="border-slate-200" data-testid="service-demand">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-purple-600" /> Service Type Demand
            </CardTitle>
          </CardHeader>
          <CardContent>
            {serviceDemand && serviceDemand.length > 0 ? (
              <div className="space-y-2">
                {serviceDemand.map((s) => (
                  <div key={s.service} className="flex items-center gap-3">
                    <span className="text-xs font-medium text-slate-700 w-20 shrink-0">{s.service}</span>
                    <div className="flex-1">
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
                        <div className="h-full bg-indigo-500 rounded-l-full" style={{ width: `${Math.min((s.active / Math.max(s.total, 1)) * 100, 100)}%` }} title={`Active: ${s.active}`} />
                        <div className="h-full bg-emerald-500" style={{ width: `${Math.min((s.placed / Math.max(s.total, 1)) * 100, 100)}%` }} title={`Placed: ${s.placed}`} />
                      </div>
                    </div>
                    <div className="text-right shrink-0 w-16">
                      <span className="text-xs font-medium text-slate-900">{s.total}</span>
                      <span className="text-[10px] text-slate-400 ml-1">({s.conversion_pct}%)</span>
                    </div>
                  </div>
                ))}
                <div className="flex gap-4 text-[10px] text-slate-500 mt-2 justify-end">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-indigo-500 rounded-full" /> Active</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full" /> Placed</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No service data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Network Metrics Trending */}
        <Card className="border-slate-200" data-testid="network-trending">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="w-4 h-4 text-cyan-600" /> Network Trending
              </CardTitle>
              <Select value={trendDays} onValueChange={setTrendDays}>
                <SelectTrigger className="w-20 h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7d</SelectItem>
                  <SelectItem value="30">30d</SelectItem>
                  <SelectItem value="90">90d</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {networkMetrics?.data && networkMetrics.data.length > 0 ? (
              <div>
                {/* Summary */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-indigo-50 p-2 rounded text-center">
                    <p className="text-lg font-bold text-indigo-700">{netSummary.total_created || 0}</p>
                    <p className="text-[10px] text-slate-500">Created</p>
                  </div>
                  <div className="bg-emerald-50 p-2 rounded text-center">
                    <p className="text-lg font-bold text-emerald-700">{netSummary.total_placed || 0}</p>
                    <p className="text-[10px] text-slate-500">Placed</p>
                  </div>
                  <div className="bg-cyan-50 p-2 rounded text-center">
                    <p className="text-lg font-bold text-cyan-700">{netSummary.conversion_rate_pct || 0}%</p>
                    <p className="text-[10px] text-slate-500">Conversion</p>
                  </div>
                </div>

                {/* Mini sparkline-style bars */}
                <div className="space-y-1">
                  <p className="text-[10px] text-slate-500 mb-1">Daily Activity</p>
                  <div className="flex items-end gap-0.5 h-12">
                    {networkMetrics.data.slice(-30).map((d, i) => {
                      const maxEvents = Math.max(...networkMetrics.data.map(x => x.workflow_events || 0), 1);
                      const height = Math.max(((d.workflow_events || 0) / maxEvents) * 100, 4);
                      return (
                        <div
                          key={i}
                          className="flex-1 bg-indigo-400 rounded-t hover:bg-indigo-600 transition-colors cursor-default"
                          style={{ height: `${height}%` }}
                          title={`${d.date}: ${d.referrals_created} created, ${d.placements} placed, ${d.workflow_events} events`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-[9px] text-slate-400">
                    <span>{networkMetrics.data[0]?.date}</span>
                    <span>{networkMetrics.data[networkMetrics.data.length - 1]?.date}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-slate-400">No trending data yet</p>
                <p className="text-xs text-slate-400 mt-1">Data will appear as referrals flow through the system</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 5: Status + Urgency Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Referrals by Status</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(ref.by_status || {}).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(ref.by_status).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 capitalize">{status}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min((count / Math.max(ref.total, 1)) * 100, 100)}%` }} />
                      </div>
                      <span className="text-sm font-medium text-slate-900 w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400">No referral data yet</p>}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Referrals by Urgency</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(ref.by_urgency || {}).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(ref.by_urgency).map(([urgency, count]) => {
                  const colors = { routine: "bg-slate-400", urgent: "bg-amber-500", emergent: "bg-red-500" };
                  return (
                    <div key={urgency} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {urgency === "emergent" && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                        {urgency === "urgent" && <Clock className="w-3.5 h-3.5 text-amber-500" />}
                        <span className="text-sm text-slate-600 capitalize">{urgency}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${colors[urgency] || "bg-slate-400"} rounded-full`} style={{ width: `${Math.min((count / Math.max(ref.total, 1)) * 100, 100)}%` }} />
                        </div>
                        <span className="text-sm font-medium text-slate-900 w-8 text-right">{count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-400">No urgency data yet</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NexusDashboard;
